import React from 'react';
import { TabbedContent } from '../components/Tabs';
import { CodeBlock } from '../components/CodeBlock';
import { Package, Cpu } from 'lucide-react';

const Installation: React.FC = () => {
  return (
    <div className="space-y-8 animate-in slide-in-from-bottom-4 duration-500">
      <div className="space-y-4">
        <h1 className="text-5xl font-black tracking-tighter text-slate-900">Installation</h1>
        <p className="text-lg text-slate-600 font-medium leading-relaxed">
          Get started by adding the Cyxor Collector agent to your project using your favorite package manager.
        </p>
      </div>

      <TabbedContent 
        tabs={[
          {
            label: "Node.js (NPM)",
            value: "npm",
            content: (
              <div className="space-y-4">
                <p className="text-sm">Standard Node.js and Express environments.</p>
                <CodeBlock 
                  language="bash" 
                  code="npm install @cyxor/collector" 
                  filename="Terminal"
                />
                <div className="bg-amber-50 border border-amber-200 p-4 rounded-xl text-xs text-amber-800 leading-relaxed font-medium">
                  <strong className="text-amber-900">Note:</strong> Requires Node.js 18.x or higher for native fetch and module support.
                </div>
              </div>
            )
          },
          {
            label: "Java (Maven)",
            value: "maven",
            content: (
              <div className="space-y-4">
                <p className="text-sm">Add the dependency to your `pom.xml` file.</p>
                <CodeBlock 
                  language="xml" 
                  code={`<dependency>\n  <groupId>com.cyxor</groupId>\n  <artifactId>cyxor-collector</artifactId>\n  <version>1.0.0</version>\n</dependency>`} 
                  filename="pom.xml"
                />
              </div>
            )
          },
          {
            label: "Rust (Cargo)",
            value: "cargo",
            content: (
              <div className="space-y-4">
                <p className="text-sm">Install the crate for high-performance systems.</p>
                <CodeBlock 
                  language="bash" 
                  code="cargo add cyxor-collector" 
                  filename="Terminal"
                />
              </div>
            )
          }
        ]}
      />

      <section className="pt-12 space-y-6 border-t border-slate-100 mt-12">
        <h2 className="text-3xl font-black text-slate-900 tracking-tighter">Next Steps</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="glass p-6 rounded-2xl flex items-center gap-4 group hover:bg-primary/5 transition-all">
            <div className="w-12 h-12 rounded-xl bg-accent flex items-center justify-center group-hover:scale-110 transition-transform">
              <Package size={24} className="text-primary" />
            </div>
            <div>
              <h4 className="font-extrabold text-slate-900 underline decoration-primary/20 hover:decoration-primary">Usage Guides</h4>
              <p className="text-xs text-slate-500 mt-1 leading-relaxed font-bold">Learn how to integrate the SDK into your code.</p>
            </div>
          </div>
          <div className="glass p-6 rounded-2xl flex items-center gap-4 group hover:bg-blue-500/5 transition-all">
            <div className="w-12 h-12 rounded-xl bg-accent flex items-center justify-center group-hover:scale-110 transition-transform">
              <Cpu size={24} className="text-blue-500" />
            </div>
            <div>
              <h4 className="font-extrabold text-slate-900 underline decoration-blue-500/20 hover:decoration-blue-500">API Reference</h4>
              <p className="text-xs text-slate-500 mt-1 leading-relaxed font-bold">Explore full documentation for every function.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Installation;
